
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { Application } from '@/types/database.types';
import { PEOProviderSelector } from './peo-preferences/PEOProviderSelector';

// Extend the Application type to include our new fields
interface ExtendedApplication extends Application {
  ideal_decision_date?: string | null;
  ideal_go_live_date?: string | null;
  preferred_api_integrations?: string[] | null;
  top_peo_selection_priorities?: string[] | null;
  additional_notes?: string | null;
}

interface PEOPreferencesProps {
  data: Partial<ExtendedApplication>;
  onSave: (data: Partial<ExtendedApplication>) => void;
}

export const PEOPreferences = ({ data, onSave }: PEOPreferencesProps) => {
  const { register, handleSubmit, reset, watch, setValue } = useForm<Partial<ExtendedApplication>>({
    defaultValues: {
      needs_guidance: data.needs_guidance || false,
      desired_medical_carriers: data.desired_medical_carriers || [],
      desired_ancillary_carriers: data.desired_ancillary_carriers || [],
      preferred_peos: data.preferred_peos || [],
      excluded_peos: data.excluded_peos || [],
      open_to_peo_benefits: data.open_to_peo_benefits ?? true,
      open_to_peo_workers_comp: data.open_to_peo_workers_comp ?? true,
      open_to_peo_401k: data.open_to_peo_401k ?? true,
      open_to_peo_epli: data.open_to_peo_epli ?? true,
      open_to_peo_ancillary: data.open_to_peo_ancillary ?? true,
      open_to_peo_eap: data.open_to_peo_eap ?? true,
      ideal_decision_date: data.ideal_decision_date || '',
      ideal_go_live_date: data.ideal_go_live_date || '',
      preferred_api_integrations: data.preferred_api_integrations || [],
      top_peo_selection_priorities: data.top_peo_selection_priorities || [],
      additional_notes: data.additional_notes || '',
    },
  });

  const preferredPeos = watch('preferred_peos') || [];
  const excludedPeos = watch('excluded_peos') || [];

  useEffect(() => {
    reset(data);
  }, [data, reset]);

  const onSubmit = handleSubmit((formData) => {
    onSave(formData);
  });

  const handleTogglePreferred = (providerName: string) => {
    const currentPreferred = preferredPeos || [];
    const updatedPreferred = currentPreferred.includes(providerName)
      ? currentPreferred.filter(name => name !== providerName)
      : [...currentPreferred, providerName];
    
    setValue('preferred_peos', updatedPreferred);
    
    // Trigger save after a brief delay
    setTimeout(() => {
      onSubmit();
    }, 100);
  };

  const handleToggleExcluded = (providerName: string) => {
    const currentExcluded = excludedPeos || [];
    const updatedExcluded = currentExcluded.includes(providerName)
      ? currentExcluded.filter(name => name !== providerName)
      : [...currentExcluded, providerName];
    
    setValue('excluded_peos', updatedExcluded);
    
    // Trigger save after a brief delay
    setTimeout(() => {
      onSubmit();
    }, 100);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      onSubmit();
    }, 100);
  };

  return (
    <form onChange={onSubmit} onSubmit={(e) => e.preventDefault()}>
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
            <h2 className="text-2xl font-semibold">PEO Preferences</h2>
          </div>
          
          <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-start gap-3">
            <p className="text-muted-foreground text-sm">
              This is the place to highlight what really matters—the specific needs and preferences help us deliver PEO matches that truly fit the organization's goals and guardrails.
            </p>
          </div>

          <div className="space-y-6">
            {/* Timeline Preferences */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Timeline Preferences</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="ideal_decision_date">Ideal Decision Date</Label>
                  <Input
                    id="ideal_decision_date"
                    type="date"
                    {...register('ideal_decision_date')}
                    onBlur={handleInputBlur}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ideal_go_live_date">Ideal Go-Live Date</Label>
                  <Input
                    id="ideal_go_live_date"
                    type="date"
                    {...register('ideal_go_live_date')}
                    onBlur={handleInputBlur}
                  />
                </div>
              </div>
            </div>

            {/* Integration Preferences */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Integration Preferences</h3>
              <div className="space-y-2">
                <Label htmlFor="preferred_api_integrations">Preferred API Integrations</Label>
                <Input
                  id="preferred_api_integrations"
                  placeholder="e.g., QuickBooks, Xero, SSO (comma-separated)"
                  {...register('preferred_api_integrations')}
                  onBlur={handleInputBlur}
                />
                <p className="text-sm text-muted-foreground">
                  List any systems you need the PEO to integrate with, separated by commas
                </p>
              </div>
            </div>

            {/* Selection Priorities */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Selection Priorities</h3>
              <div className="space-y-2">
                <Label htmlFor="top_peo_selection_priorities">What matters most to you when evaluating PEO providers?</Label>
                <Textarea
                  id="top_peo_selection_priorities"
                  placeholder="e.g., Cost savings, HR expertise, Technology platform, Customer service..."
                  rows={3}
                  {...register('top_peo_selection_priorities')}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>

            {/* Provider Preferences */}
            <div>
              <h3 className="text-lg font-medium mb-2">Provider Preferences</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Search for PEO providers and mark them as preferred (✓) or excluded (✗). 
                Preferred providers will be prioritized in recommendations, while excluded providers will not be considered.
              </p>
              
              <PEOProviderSelector
                preferredProviders={preferredPeos}
                excludedProviders={excludedPeos}
                onTogglePreferred={handleTogglePreferred}
                onToggleExcluded={handleToggleExcluded}
              />
            </div>

            {(preferredPeos.length > 0 || excludedPeos.length > 0) && (
              <div className="space-y-4">
                {preferredPeos.length > 0 && (
                  <div>
                    <h4 className="font-medium text-green-700 mb-2">
                      Preferred Providers ({preferredPeos.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {preferredPeos.map((provider) => (
                        <span
                          key={provider}
                          className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                        >
                          {provider}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {excludedPeos.length > 0 && (
                  <div>
                    <h4 className="font-medium text-red-700 mb-2">
                      Excluded Providers ({excludedPeos.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {excludedPeos.map((provider) => (
                        <span
                          key={provider}
                          className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full"
                        >
                          {provider}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Additional Notes */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Additional Information</h3>
              <div className="space-y-2">
                <Label htmlFor="additional_notes">Is there anything else we should know?</Label>
                <Textarea
                  id="additional_notes"
                  placeholder="Share any additional context, special requirements, or considerations that would help us find the best PEO match for your organization..."
                  rows={4}
                  {...register('additional_notes')}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};
